import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';

// Auth pages
import LoginPage from './pages/LoginPage';

// Admin pages
import AdminDevicesPage from './pages/admin/DevicesPage';
import AdminUsersPage from './pages/admin/UsersPage';

// Parent pages
import DashboardPage from './pages/DashboardPage';
import ChildrenPage from './pages/parent/ChildrenPage';
import AddChildPage from './pages/parent/AddChildPage';
import ChildDetailPage from './pages/parent/ChildDetailPage';
import TrajectoryPage from './pages/parent/TrajectoryPage';
import ZonesPage from './pages/parent/ZonesPage';
import RelativesPage from './pages/parent/RelativesPage';
import LogsPage from './pages/parent/LogsPage';
import SettingsPage from './pages/SettingsPage';

// ===== ROUTE GUARDS =====
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 60, textAlign: 'center', fontFamily: "'Nunito',sans-serif" }}>Yuklanmoqda...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function ParentRoute({ children }) {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Admin also can access parent routes
  return children;
}

// ===== MAIN ROUTES =====
function AppRoutes() {
  const { isAdmin, isOwner } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />

      {/* Protected routes with Layout */}
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        {/* Dashboard - different for admin vs parent */}
        <Route index element={<DashboardPage />} />

        {/* Admin-only routes */}
        <Route path="admin/devices" element={<AdminRoute><AdminDevicesPage /></AdminRoute>} />
        <Route path="admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />

        {/* Parent routes (admin can also access) */}
        <Route path="children" element={<ParentRoute><ChildrenPage /></ParentRoute>} />
        <Route path="children/add" element={<ParentRoute><AddChildPage /></ParentRoute>} />
        <Route path="children/:id" element={<ParentRoute><ChildDetailPage /></ParentRoute>} />
        <Route path="children/:id/trajectory" element={<ParentRoute><TrajectoryPage /></ParentRoute>} />
        <Route path="zones" element={<ParentRoute><ZonesPage /></ParentRoute>} />
        <Route path="relatives" element={<ParentRoute><RelativesPage /></ParentRoute>} />
        <Route path="logs" element={<ParentRoute><LogsPage /></ParentRoute>} />

        {/* Settings for all */}
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
